/**
 * Web Component entry point for the Community Header widget.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/community-header-web-component';
 *
 * // Then use in HTML:
 * // <legion-community-header api-url="https://your-domain.com" title="Community" />
 * ```
 */

import { HEADER_THEMING_PROP_TO_PARAM } from "./types";
import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

const HEADER_CSS_ATTRIBUTES = Object.values(HEADER_THEMING_PROP_TO_PARAM);

class LegionCommunityHeaderElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url", "title", "show-search", "show-stats", "show-online",
      "community-url", "theme", "width", "height",
      ...HEADER_CSS_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if ((AUTH_BRIDGE_OBSERVED_ATTRS as readonly string[]).includes(name) && this.iframe) {
      this.authBridge?.postCurrentToken();
      return;
    }
    this.render();
  }

  private get apiUrl(): string { return this.getAttribute("api-url") || window.location.origin; }
  private get widgetTitle(): string { return this.getAttribute("title") || "Community"; }
  private get showSearch(): boolean { return this.getAttribute("show-search") !== "false"; }
  private get showStats(): boolean { return this.getAttribute("show-stats") !== "false"; }
  private get showOnline(): boolean { return this.getAttribute("show-online") !== "false"; }
  private get communityUrl(): string | null { return this.getAttribute("community-url"); }
  private get theme(): string { return this.getAttribute("theme") || "light"; }
  private get widgetWidth(): string { return this.getAttribute("width") || "100%"; }
  private get widgetHeight(): string { return this.getAttribute("height") || "auto"; }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/community-header", this.apiUrl);

    url.searchParams.set("title", this.widgetTitle);
    url.searchParams.set("showSearch", String(this.showSearch));
    url.searchParams.set("showStats", String(this.showStats));
    url.searchParams.set("showOnline", String(this.showOnline));
    url.searchParams.set("theme", this.theme);

    if (this.communityUrl) {
      url.searchParams.set("communityUrl", this.communityUrl);
    }

    HEADER_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    const resolvedHeight = this.widgetHeight === "auto" ? "120px" : this.widgetHeight;

    iframe.src = url.toString();
    iframe.width = this.widgetWidth;
    iframe.height = resolvedHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Community Header Widget";
    iframe.loading = "lazy";

    iframe.onload = () => {
      this.dispatchEvent(new CustomEvent("load", {
        detail: { widget: "community-header", theme: this.theme },
      }));
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load community header widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host { display: block; width: ${this.widgetWidth.includes("%") ? this.widgetWidth : this.widgetWidth + "px"}; height: ${resolvedHeight}; }
      iframe { width: 100%; height: 100%; border: none; border-radius: 8px; }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (typeof customElements !== "undefined" && !customElements.get("legion-community-header")) {
  customElements.define("legion-community-header", LegionCommunityHeaderElement);
}

export { LegionCommunityHeaderElement };

